'use client'

import { Composite, CompositeItem } from '@floating-ui/react'
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { cn, Input, InputWrapper } from 'design-system'
import { FC, useMemo, useState, type JSX } from 'react'

import { CompositeHTMLProps, FILTER_ITEM_CLASS_NAME } from './constants'
import { TreeFilterConfig, TreeFilterItem } from './types'

type TreeSubFilterProps = {
  config: TreeFilterConfig
  value: string[]
  onChange: (value: string[]) => void
}

// Multi-select checkbox list shaped as a tree: children indent under their
// parent with collapsible chevrons. Checking a node selects only that node —
// selection semantics match the flat select/search filters.
export const TreeSubFilter: FC<TreeSubFilterProps> = ({
  config,
  value,
  onChange
}) => {
  const [search, setSearch] = useState('')
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())

  const { roots, childrenOf } = useMemo(() => {
    const ids = new Set(config.items.map(i => i.id))
    const byParent = new Map<string, TreeFilterItem[]>()
    const rootItems: TreeFilterItem[] = []
    for (const item of config.items) {
      if (item.parentId && ids.has(item.parentId)) {
        const siblings = byParent.get(item.parentId) ?? []
        siblings.push(item)
        byParent.set(item.parentId, siblings)
      } else {
        rootItems.push(item)
      }
    }
    return { roots: rootItems, childrenOf: byParent }
  }, [config.items])

  // While searching, a node stays visible if it matches or sits on the path to
  // a match (ancestor for context, descendant because a parent match implies
  // its subtree), and the tree is force-expanded so matches can't hide.
  const visibleIds = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return null
    const visible = new Set<string>()
    const walk = (item: TreeFilterItem, underMatch: boolean): boolean => {
      const matched = underMatch || item.label.toLowerCase().includes(term)
      let inSubtree = false
      for (const child of childrenOf.get(item.id) ?? []) {
        inSubtree = walk(child, matched) || inSubtree
      }
      if (matched || inSubtree) {
        visible.add(item.id)
        return true
      }
      return false
    }
    for (const root of roots) walk(root, false)
    return visible
  }, [search, roots, childrenOf])

  const toggleItem = (id: string) => {
    const next = value.includes(id)
      ? value.filter(v => v !== id)
      : [...value, id]
    onChange(next)
  }

  const toggleCollapse = (id: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderItems = (items: TreeFilterItem[], level: number): JSX.Element[] =>
    items.flatMap(item => {
      if (visibleIds && !visibleIds.has(item.id)) return []
      const children = childrenOf.get(item.id) ?? []
      const isCollapsed = !visibleIds && collapsedIds.has(item.id)
      const checked = value.includes(item.id)
      const row = (
        <div
          key={item.id}
          className="flex items-center"
          style={{ paddingLeft: level * 16 }}>
          {children.length > 0 ? (
            // Mouse affordance only (tabIndex -1): keyboard users collapse and
            // expand with the arrow keys on the row itself.
            <button
              type="button"
              tabIndex={-1}
              onClick={() => toggleCollapse(item.id)}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${item.label}`}
              className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100">
              {isCollapsed ? (
                <ChevronRight size={14} aria-hidden />
              ) : (
                <ChevronDown size={14} aria-hidden />
              )}
            </button>
          ) : (
            <span className="w-6 shrink-0" aria-hidden />
          )}
          <CompositeItem
            render={(htmlProps: CompositeHTMLProps) => (
              <button
                {...htmlProps}
                className={cn(FILTER_ITEM_CLASS_NAME, {
                  'bg-blue-100': htmlProps['data-active'] !== undefined
                })}
                role="option"
                aria-selected={checked}
                onClick={() => toggleItem(item.id)}
                onKeyDown={e => {
                  if (children.length === 0 || visibleIds) return
                  if (e.key === 'ArrowRight' && isCollapsed) {
                    toggleCollapse(item.id)
                  } else if (e.key === 'ArrowLeft' && !isCollapsed) {
                    toggleCollapse(item.id)
                  }
                }}>
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    checked
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-slate-300 bg-white'
                  )}
                  aria-hidden>
                  {checked && <Check size={12} className="text-white" />}
                </span>
                <span className="truncate text-left">{item.label}</span>
              </button>
            )}
          />
        </div>
      )
      return [row, ...(isCollapsed ? [] : renderItems(children, level + 1))]
    })

  const rows = renderItems(roots, 0)

  return (
    <div className="flex flex-col gap-1 p-2">
      <InputWrapper inputSize="sm" leftIconSlot={<Search />}>
        <Input
          type="text"
          placeholder={`Search ${config.label.toLowerCase()}`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </InputWrapper>
      {rows.length === 0 ? (
        <p className="p-2 text-sm text-slate-500">No matches</p>
      ) : (
        <Composite
          orientation="vertical"
          className="flex max-h-60 flex-col gap-1 overflow-y-auto"
          role="listbox"
          aria-label={config.label}
          aria-multiselectable>
          {rows}
        </Composite>
      )}
    </div>
  )
}
